import * as Sentry from '@sentry/react';
import { publishCurrentOrg, publishSignedOut } from '../helpers/orgSessionCookie';
import {
  AUTH_TOKEN,
  CURRENT_USER,
  CURRENT_USER_ID,
  EG_SUBMISSION,
  EMAIL_VERIFIED,
  FREE_TRIAL_POPUP_SHOWN_KEY,
  GUEST_USER,
  INVITE_DATA,
  LATEST_TNC_ACCEPTED,
  MEMBER_ID,
  ORG_OWNER_DETAIL,
  REFRESH_TOKEN,
} from '../constants/projectConstants';
import _, { isEqual } from 'lodash';
import {
  apiCheckGuestUserPermission,
  apiGetMemberOrganizationPermissions,
  apiGetOnlyOrganizationPermissions,
  apiGetOrganizationDetails,
  apiGetUserDetails,
  apiPostLogout,
} from '../helpers/api';
import { differenceInCalendarDays, format } from 'date-fns';
import {
  clearLocalStorage,
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
  wait,
} from '../helpers/jsHelper';
import { createContext, memo, useContext, useEffect, useMemo, useState } from 'react';
import {
  selectTotalEditors,
  selectUserActiveSubscriptionSelfServePlan,
  selectUserPlanList,
} from '../redux/slicers/subscriptionSlicer';

import { GAMIFIED_ACTIVE } from '../components/gamificationTooltip/constants';
import { IMAGE_SEARCH_ONBOARDING_STEPS } from '../common/TooltipOverlay/TooltipOverlay';
import { CURATOR_PRODUCT_BUILDER_ROUTE, SIGN_IN_ROUTE } from '../constants/routePathConstants';
import { curatorActions } from '../redux/slicers/admin/curatorSlicer';
import { dispatcher } from '../helpers/projectHelper';
import { getAppState } from '../redux/store';
import { useLocation } from 'react-router-dom';
import { warningToast } from '../helpers/toastHelper.js';
import { deleteCookies } from '../helpers/apiHelper.js';
import { PERMISSIONS } from '../constants/permissionConstants.js';
import { setFrappeRequestData } from '../redux/slicers/activitySlicer.js';

export const FREEMIUM_TYPE = 'FREEMIUM';
export const OLD_SUBSCRIPTION_TYPE = 'OLD_SUBSCRIPTION';
export const PREMIUM_TYPE = 'PREMIUM';

const OLD_SUBSCRIPTION_PLAN = 'Old Subscription';
const PREMIUM_PLANS = [
  'Basic Monthly',
  'Basic Yearly',
  'Plus Monthly',
  'Plus Yearly',
  'Pro Monthly',
  'Pro Yearly',
];

const initialGuessUserAccess = {
  read: false,
  write: false,
  loading: true, // we are keeping the loading default value to true because it help to wait for api call
};

const UserContext = createContext({
  authorized: null,
  currentUser: {},
  setCurrentUser: () => {},
  orgOwnerDetail: {},
  setOrgOwnerDetail: () => {},
  currentUserId: null,
  setCurrentId: () => {},
  token: null,
  setToken: () => {},
  refreshToken: null,
  setRefreshToken: () => {},
  verificationToken: null,
  setVerificationToken: () => {},
  onLogin: () => {},
  logout: () => {},
  latestTNCAccepted: false,
  setLatestTNCAccepted: () => {},
  emailVerified: false,
  canAccessLiveOnAppData: () => {},
  canAccessLiveOnApp: () => {},
  canCreatePropGroup: () => {},
  canCreateUserpilotOnboardingSteps: false,
  isInternalOrg: () => false,
  setEmailVerified: () => {},
  changeProfilePic: () => {},
  changeOrgProfilePic: () => {},
  addCreatedAtDate: () => {},
  changeWatermarkPic: () => {},
  inviteData: {},
  setInviteData: () => {},
  updateUserData: () => {},
  isUpdatingUserDetails: false,
  freeCredits: 0,
  organizationType: () => null,
  isFreemiumUser: () => false,
  isPremiumUser: () => false,
  isOldSubscriptionUser: () => false,
  isFreemiumOrOldSubscriptionUser: false,
  isFreemiumOrSelfServePlan: () => false,
  isPremiumAndSelfServePlan: () => false,
  isGuestUser: () => false,
  setGuestUserAccess: () => {},
  guestUserAccess: initialGuessUserAccess,
  updateGuestUserAccess: () => {},
  subscription_end_date: null,
  subscription_expiry: false,
  showAssignPackModal: false,
  setShowAssignPackModal: () => {},
  isPackOrganization: false,
  isUnlimitedRendering: false,
  userRegisteredOn: null,
  isMemberHaveEditorAccess: false,
  logoutLoading: false,
  permissionsMap: {},
  organizationPermissionsMap: {},
  isNewSubscriptionPlan: false,
  isGuestLogin: false,
  isSalsifyConnected: false,
  canUpdatePdpV1Content: false,
});

const userData = { organization: null, user: null, id: null, currentUser: null };
export const getCurrentUserOrganization = () => userData.organization;
export const getCurrentUserUserId = () => userData.user?.id;
export const getCurrentUserId = () => userData.id;
export const getCurrentUser = () => userData.currentUser;

export const checkIfMemberHaveEditorAccessAsync = async (showMessage = true) => {
  await wait(2000);

  const user = await getLocalStorage(CURRENT_USER, true);
  const subscription = user?.organization_detail?.subscription_type?.toUpperCase();
  const isOwner = user?.role?.is_owner;
  const totalEditors = await selectTotalEditors(getAppState());
  const userPlans = await selectUserPlanList(getAppState());
  const noMainSubscriptionData = userPlans?.length === 0;

  if (
    (subscription === FREEMIUM_TYPE &&
      ((isOwner && totalEditors === 0) || user?.is_editor_access)) ||
    subscription === OLD_SUBSCRIPTION_TYPE ||
    (subscription === PREMIUM_TYPE && (noMainSubscriptionData || user?.is_editor_access))
  ) {
    return true;
  }
  showMessage && warningToast("You don't have an editor access.");
  return false;
};

export const checkIfMemberHaveEditorAccess = (showMessage = true) => {
  const user = getLocalStorage(CURRENT_USER, true);
  const subscription = user?.organization_detail?.subscription_type?.toUpperCase();
  const isOwner = user?.role?.is_owner;
  const totalEditors = selectTotalEditors(getAppState());
  const userPlans = selectUserPlanList(getAppState());
  const noMainSubscriptionData = userPlans?.length === 0;

  if (
    (subscription === FREEMIUM_TYPE &&
      ((isOwner && totalEditors === 0) || user?.is_editor_access)) ||
    subscription === OLD_SUBSCRIPTION_TYPE ||
    (subscription === PREMIUM_TYPE && (noMainSubscriptionData || user?.is_editor_access))
  ) {
    return true;
  }
  showMessage && warningToast("You don't have an editor access.");
  return false;
};

export const checkIfEmailIsVerifiedOrNot = (showMessage = true) => {
  const isVerified = getLocalStorage(EMAIL_VERIFIED, true);
  if (isVerified) {
    return true;
  }
  showMessage &&
    warningToast(
      'Please verify your email id, We have sent an email on your registered email id to verify your account.'
    );
  return false;
};

export const checkIfEmailIsVerifiedOrNotFromApi = async (showMessage = true) => {
  let isVerified = getLocalStorage(EMAIL_VERIFIED, true);
  if (!isVerified) {
    const response = await apiGetUserDetails();
    isVerified = response?.is_email_verified;
    setLocalStorage(EMAIL_VERIFIED, isVerified);
  }
  if (isVerified) {
    return true;
  }
  showMessage &&
    warningToast(
      'Please verify your email id, We have sent an email on your registered email id to verify your account.'
    );
  return false;
};

var isUserDataUpdating = false;
var isUserPermissionsUpdating = false;

const UserContextProvider = memo(({ children }) => {
  const location = useLocation();
  const pathname = location?.pathname;
  const authToken = getLocalStorage(AUTH_TOKEN);
  const currentUserIdInfo = getLocalStorage(CURRENT_USER_ID);
  const refresh_token = getLocalStorage(REFRESH_TOKEN);
  const email_verified = getLocalStorage(EMAIL_VERIFIED);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const latest_tnc_accepted = getLocalStorage(LATEST_TNC_ACCEPTED);
  const invite_data = getLocalStorage(INVITE_DATA, true);
  const [token, setToken] = useState(authToken || null);
  const user = token ? getLocalStorage(CURRENT_USER, true) : null;
  const [refreshToken, setRefreshToken] = useState(refresh_token || null);
  const [emailVerified, setEmailVerified] = useState(email_verified || false);
  const [latestTNCAccepted, setLatestTNCAccepted] = useState(latest_tnc_accepted || false);
  const [currentUser, _setCurrentUser] = useState(user);
  const orgOwner = token ? getLocalStorage(ORG_OWNER_DETAIL, true) : null;
  const [orgOwnerDetail, _setOrgOwnerDetail] = useState(orgOwner);
  const [currentUserId, setCurrentUserId] = useState(currentUserIdInfo);
  const [verificationToken, setVerificationToken] = useState(null);
  const [inviteData, setInviteData] = useState(invite_data || {});
  const [isMounted, setIsMounted] = useState(false);
  const [orgRegisteredOn, setOrgRegisteredOn] = useState(null);
  const [guestUserAccess, setGuestUserAccess] = useState(initialGuessUserAccess);
  const [showAssignPackModal, setShowAssignPackModal] = useState(false);
  const [memberPermissionList, setMemberPermissionList] = useState([]);
  const [isUpdatingUserDetails, setIsUpdatingUserDetails] = useState(false);

  useEffect(async () => {
    userData.currentUser = currentUser;
    userData.organization = currentUser?.organization || null;
    userData.user = currentUser?.user || null;
    userData.id = currentUser?.id || null;
    await updateGuestUserAccess();
    await updateUserData();
    setIsMounted(true);
    let timer = null;
    const storageEventHandler = () => {
      const user = getLocalStorage(CURRENT_USER, true);
      if (!isEqual(user, currentUser) && isCuratorOnboardingActive()) {
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          setCurrentUser(user);
        }, 500);
      }
    };
    window.addEventListener('storage', storageEventHandler);
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      window.removeEventListener('storage', storageEventHandler);
    };
  }, []);

  // Tell the other *.imagine.io apps which organization is signed in - AI Studio holds copies of
  // our tokens and can't see a switch by itself. See `orgSessionCookie`.
  useEffect(() => {
    if (currentUser?.organization) publishCurrentOrg(currentUser.organization);
  }, [currentUser?.organization]);

  useEffect(() => {
    const fetchOrg = async () => {
      if (currentUser?.organization) {
        const res = await apiGetOrganizationDetails();
        if (res?.member_permissions) {
          setMemberPermissionList(res?.member_permissions);
        }
        res?.created_at && setOrgRegisteredOn(res?.created_at);
        if (res) {
          setTimeout(() => {
            // functional update: merges onto whatever currentUser is *at fire time*,
            // not the stale snapshot this effect closed over - a plain spread here
            // was clobbering fresher fields (e.g. free trial data from
            // updateUserData(), which runs around the same time on login/nav) with
            // this 300ms-delayed, stale copy of currentUser
            _setCurrentUser((prevUser) => ({
              ...prevUser,
              allow_expired_textures: res?.allow_expired_textures || false,
            }));
          }, 300);
        }
      }
    };

    let timeId;
    // If the current page is the Mattress Builder page, set a timeout to fetch organization details
    // preventing unnecessary API call on mattress builder curator page
    if (window.location.pathname.includes(CURATOR_PRODUCT_BUILDER_ROUTE.replace(':id', ''))) {
      timeId = setTimeout(() => fetchOrg(), 10000);
    } else fetchOrg();

    return () => {
      if (timeId) clearTimeout(timeId);
    };
  }, [currentUser?.organization]);

  const isCuratorOnboardingActive = () => {
    const count = getLocalStorage(GAMIFIED_ACTIVE, true);
    return count > 0;
  };

  const setCurrentUser = (user = {}) => {
    if (user.organization) {
      userData.currentUser = user;
      userData.organization = user.organization;
      userData.user = user.user;
      userData.id = user.id;
    }
    _setCurrentUser({ ...currentUser, ...user });
    setLocalStorage(CURRENT_USER, { ...currentUser, ...user });
    setLocalStorage(MEMBER_ID, user.id);
    //set sentry data
    Sentry.setUser({
      id: user?.id,
      email: user?.user?.email,
      name: user?.user?.name,
      organization: user?.organization_detail?.name,
    });
  };

  const setOrgOwnerDetail = (owner) => {
    const _owner = {
      id: owner?.id,
      email: owner?.email,
      name: owner?.name,
      first_name: owner?.first_name,
      last_name: owner?.last_name,
    };

    _setOrgOwnerDetail(_owner);
    setLocalStorage(ORG_OWNER_DETAIL, _owner);
  };

  const updateUserData = async (data = null) => {
    if (currentUser && !isUserDataUpdating) {
      let userObj = {};
      if (!data) {
        isUserDataUpdating = true;
        setIsUpdatingUserDetails(true);
        const [res, permissions, organizationPermissions] = await Promise.all([
          apiGetUserDetails(),
          apiGetMemberOrganizationPermissions(),
          apiGetOnlyOrganizationPermissions(),
        ]);
        // We added the `is_old_subscription` key to manage the mattress builder.
        // The available counts are displayed based on this key.
        // If this key is true, we show the 3D modal variable.
        // Otherwise, we show the 3D modal upload limit count.
        if (res && Object.entries(res).length > 0) {
          const {
            profile_image,
            email,
            is_email_verified,
            first_name,
            last_name,
            mobile_number,
            created_at,
            other_info,
            is_editor_access,
            is_editor,
            can_create_onboard_steps,
            is_prop_group_access,
            is_live_on_app,
            can_download_3d_file,
            can_overwrite_prop_group,
            is_new_subscription_plan,
            is_old_subscription,
            is_guest,
            user_pilot_id,
            was_guest_user,
            can_use_workflow_builder,
          } = res;

          const {
            id: orgId,
            watermark_on_rendering,
            name,
            free_credits,
            subscription_type,
            subscription_name,
            subscription_expiry,
            subscription_end_date,
            is_salsify_org,
            thumbnail,
            status,
            is_pack_organization,
            is_unlimited_rendering,
            model_upload_limit,
            is_unlimited_texture_upload,
            allow_expired_textures,
            // free trial fields, already returned on organization_details
            is_free_trial_given,
            is_free_trial_started,
            free_trial_start_date,
            free_trial_end_date,
            is_free_trial_expired,
            free_trial_days,
          } = res?.organization_details || {};
          const {
            id: roleId,
            name: roleName,
            is_admin,
            is_external_member,
            is_internal_member,
            is_owner,
            is_staff,
          } = res?.role_details || {};
          setLocalStorage(EMAIL_VERIFIED, is_email_verified);
          setEmailVerified(is_email_verified);
          // let user = { ...currentUser }
          // user['is_editor'] = is_editor;
          userObj = {
            ...currentUser,
            ...(permissions &&
              permissions?.results?.length && { permissions_list: permissions.results }),
            ...(organizationPermissions &&
              organizationPermissions?.results?.length && {
                organization_permissions_list: organizationPermissions.results,
              }),
            is_editor_access: is_editor_access,
            is_editor: is_editor,
            can_create_onboard_steps: can_create_onboard_steps,
            can_download_3d_file: can_download_3d_file,
            can_overwrite_prop_group: can_overwrite_prop_group,
            is_new_subscription_plan: is_new_subscription_plan,
            is_old_subscription: is_old_subscription,
            is_guest: is_guest,
            is_prop_group_access: is_prop_group_access,
            is_live_on_app: is_live_on_app,
            can_use_workflow_builder: can_use_workflow_builder,
            user_pilot_id,
            was_guest_user,
            organization: orgId || currentUser.organization_detail.id,
            user: {
              ...currentUser.user,
              profile_image: profile_image,
              email: email,
              first_name: first_name,
              last_name: last_name,
              mobile_number: mobile_number,
              userRegisteredOn: created_at,
              other_info: other_info,
            },
            organization_detail: {
              ...currentUser.organization_detail,
              free_credits: free_credits,
              subscription_type: subscription_type,
              subscription_name: subscription_name,
              subscription_expiry: subscription_expiry,
              subscription_end_date: subscription_end_date,
              is_salsify_org: is_salsify_org,
              watermark_on_rendering: watermark_on_rendering,
              is_pack_organization: is_pack_organization,
              is_unlimited_rendering: is_unlimited_rendering,
              model_upload_limit: model_upload_limit,
              is_unlimited_texture_upload: is_unlimited_texture_upload,
              allow_expired_textures: allow_expired_textures,
              status: status,
              is_free_trial_given: is_free_trial_given,
              is_free_trial_started: is_free_trial_started,
              free_trial_start_date: free_trial_start_date,
              free_trial_end_date: free_trial_end_date,
              is_free_trial_expired: is_free_trial_expired,
              free_trial_days: free_trial_days,
              ...(!isGuestUser()
                ? {
                    thumbnail: thumbnail,
                    name: name,
                    id: orgId,
                  }
                : {}),
            },
            role: {
              ...currentUser.role,
              id: roleId,
              name: roleName,
              is_admin: is_admin,
              is_external_member: is_external_member,
              is_internal_member: is_internal_member,
              is_owner: is_owner,
              is_staff: is_staff,
            },
          };

          // setting image search onboarding data
          dispatcher(
            curatorActions.setImageScanSearchOnboardingData({
              steps: IMAGE_SEARCH_ONBOARDING_STEPS,
              isActive: !other_info?.showImageSearchOnboarding,
              isCompleted: other_info?.showImageSearchOnboarding,
            })
          );
        }
      }
      // updateCurrentUser(data ? data : userObj);
      setCurrentUser(data ? data : userObj);
      isUserDataUpdating = false;
      setIsUpdatingUserDetails(false);

      return data ? data : userObj;
    }
  };

  const updateGuestUserAccess = async () => {
    const isGuest = localStorage.getItem(GUEST_USER);
    if (isGuest && isGuest === 'true') {
      const accessResponse = await apiCheckGuestUserPermission();
      if (accessResponse && accessResponse?.results) {
        setGuestUserAccess({
          read: accessResponse?.results?.[0].is_guest_read_access,
          write: accessResponse?.results?.[0].is_create_request_access,
          loading: false,
        });
      } else {
        localStorage.removeItem(GUEST_USER);
        setGuestUserAccess({
          read: false,
          write: false,
          loading: false,
        });
      }
    }
  };

  const permissionsMap = useMemo(() => {
    if (!currentUser?.permissions_list) return {};

    return currentUser.permissions_list.reduce((acc, p) => {
      acc[p.codename] = p;
      return acc;
    }, {});
  }, [currentUser?.permissions_list]);

  const organizationPermissionsMap = useMemo(() => {
    if (!currentUser?.organization_permissions_list) return {};

    return currentUser.organization_permissions_list.reduce((acc, p) => {
      acc[p.codename] = p;
      return acc;
    }, {});
  }, [currentUser?.organization_permissions_list]);

  async function logout() {
    setLogoutLoading(true);
    window?.sendDataToFrappeApp?.({ logoutFrappe: true });
    await apiPostLogout();
    dispatcher(setFrappeRequestData({ url: null }));
    deleteCookies(['HSIT', 'hubspotutk']); // deleting HSIT (Hubspot Identification Token) from cookie

    // Preventing specific keys from being cleared from localStorage
    const preventKeysFromRemoving = [EG_SUBMISSION, FREE_TRIAL_POPUP_SHOWN_KEY];
    const keysWithValues = [];

    // Store the values of the keys we want to protect
    preventKeysFromRemoving.forEach((key) => {
      if (localStorage.getItem(key) !== null) {
        keysWithValues.push({ key, value: localStorage.getItem(key) });
      }
    });

    clearLocalStorage();
    // Nobody is signed in - say so, so AI Studio ends the session its token copies won't.
    publishSignedOut();

    setLocalStorage('FRAPPE_APP_LOGOUT', 'true');

    // Restore the protected keys immediately - window.location.assign() below is a
    // full page navigation, so a delayed setTimeout here would never fire in time
    keysWithValues.forEach(({ key, value }) => {
      localStorage.setItem(key, value);
    });

    Sentry.setUser(null);
    // window.location.reload();
    window.location.assign(SIGN_IN_ROUTE);
  }

  function onLogin(
    user,
    token,
    refresh_token,
    is_email_verified = false,
    latest_tnc_accepted = false
  ) {
    if (user) setCurrentUser(user);

    if (token) {
      setToken(token);
      setLocalStorage(AUTH_TOKEN, token);
    }

    if (refresh_token) {
      setRefreshToken(refresh_token);
      setLocalStorage(REFRESH_TOKEN, refresh_token);
    }

    setEmailVerified(is_email_verified || false);
    setLocalStorage(EMAIL_VERIFIED, is_email_verified);

    setLatestTNCAccepted(latest_tnc_accepted);
    setLocalStorage(LATEST_TNC_ACCEPTED, latest_tnc_accepted);

    setInviteData({});
    removeLocalStorage(INVITE_DATA);
  }

  function changeProfilePic(image) {
    setCurrentUser({
      user: { ...currentUser.user, profile_image: image },
    });
  }

  function changeOrgProfilePic(image) {
    setCurrentUser({
      organization_detail: {
        ...currentUser.organization_detail,
        thumbnail: image,
      },
    });
  }

  function addCreatedAtDate(date) {
    setCurrentUser({
      organization_detail: {
        ...currentUser.organization_detail,
        created_at: date,
      },
    });
  }

  function changeWatermarkPic(image) {
    setCurrentUser({
      organization_detail: {
        ...currentUser.organization_detail,
        watermark_on_rendering: image,
      },
    });
  }

  const updateCurrentUser = (data) => {
    if (isMounted) setCurrentUser(data);
  };

  // who can see data created from Live on App such as imagine.io staff
  const canAccessLiveOnAppData = () => {
    return Boolean(currentUser?.organization_detail?.is_live_on_app);
  };

  // who can use Live on App
  const canAccessLiveOnApp = () => {
    return Boolean(currentUser?.is_live_on_app);
  };

  // who can create Prop Group
  const canCreatePropGroup = () => {
    return Boolean(currentUser?.is_prop_group_access);
  };

  // org level check permission for create Prop Group
  const canCreatePropGroupOrgLevel = () => {
    return Boolean(
      currentUser?.permissions_list?.some(
        ({ codename, is_active }) =>
          is_active && codename === PERMISSIONS.INVITED_MEMBER_CREATE_PRODUCT_GROUP.codename
      )
    );
  };

  // member level check permission for create Prop Group
  const canCreatePropGroupMemberLevel = () => {
    return Boolean(
      memberPermissionList?.some(
        (permissionCode) =>
          permissionCode === PERMISSIONS.INVITED_MEMBER_CREATE_PRODUCT_GROUP.codename
      )
    );
  };

  const isInternalOrg = () => {
    return Boolean(currentUser?.organization_detail?.is_internal_org);
  };

  const getMyOrgType = () => {
    return currentUser?.organization_detail?.subscription_type;
  };

  const isFreemiumUser = () => {
    const type = getMyOrgType();
    if (!type) return false;
    const _type = type.toLowerCase();
    return FREEMIUM_TYPE.toLowerCase() === _type;
  };

  const isPremiumUser = () => {
    const type = getMyOrgType();
    if (!type) return false;
    const _type = type.toLowerCase();
    return PREMIUM_TYPE.toLowerCase() === _type;
  };

  const isOldSubscriptionUser = () => {
    const type = getMyOrgType();
    if (!type) return false;
    const _type = type.toLowerCase();
    return OLD_SUBSCRIPTION_TYPE.toLowerCase() === _type;
  };

  const isFreemiumOrSelfServePlan = () => {
    const isSelfServePlan = selectUserActiveSubscriptionSelfServePlan(getAppState());
    return isFreemiumUser() || isSelfServePlan;
  };

  const isPremiumAndSelfServePlan = () => {
    const isSelfServePlan = selectUserActiveSubscriptionSelfServePlan(getAppState());
    return isPremiumUser() && isSelfServePlan;
  };

  const isGuestUser = () => {
    // we are using for Guest User ( from imagine.io team ) who loggedin into our clients organization from dashboard for request work
    const isGuest = localStorage.getItem(GUEST_USER);
    return (
      currentUser &&
      ((isGuest && isGuest === 'true') || guestUserAccess.read || guestUserAccess.write)
    );
  };

  function setIsEditor(value) {
    setCurrentUser({
      is_editor: value,
    });
  }

  const canUpdatePdpV1Content = useMemo(() => {
    const isPermissionAvailable =
      organizationPermissionsMap[PERMISSIONS.CAN_UPDATE_PDP_V1_CONTENT.codename];
    const userPlans = selectUserPlanList(getAppState());
    const noMainSubscriptionData = !userPlans?.length;
    const hasEditorAccess =
      isOldSubscriptionUser() || (isPremiumUser() && noMainSubscriptionData)
        ? true
        : currentUser?.is_editor_access;
    return (Boolean(isPermissionAvailable) && hasEditorAccess) || currentUser?.is_editor;
  }, [
    organizationPermissionsMap,
    currentUser?.is_editor_access,
    currentUser?.is_editor,
    currentUser?.organization_detail?.subscription_type,
  ]);

  // Free trial: gated on subscription_type === Freemium (master switch - upgrading
  // the org hides all trial UI regardless of what the trial fields still say).
  const freeTrialInfo = useMemo(() => {
    const orgDetail = currentUser?.organization_detail;
    const isEligiblePlan = isFreemiumUser();
    const isFreeTrialStarted = Boolean(orgDetail?.is_free_trial_started) && isEligiblePlan;
    const freeTrialStartDate = orgDetail?.free_trial_start_date || null;
    const freeTrialEndDate = orgDetail?.free_trial_end_date || null;
    // is_free_trial_expired is authoritative from the backend, not computed from dates.
    const isFreeTrialExpired = isFreeTrialStarted && Boolean(orgDetail?.is_free_trial_expired);
    const freeTrialDays = orgDetail?.free_trial_days || null;
    let freeTrialDaysRemaining = null;
    // free_trial_end_date has a +1 day grace buffer for the hard cutoff - the
    // countdown uses start date + offered days instead, so day one always reads
    // "N days left" rather than N+1.
    if (isFreeTrialStarted && freeTrialStartDate && freeTrialDays && !isFreeTrialExpired) {
      const daysElapsed = differenceInCalendarDays(new Date(), new Date(freeTrialStartDate));
      freeTrialDaysRemaining = Math.max(0, freeTrialDays - daysElapsed);
    }
    let freeTrialEndDateFormatted = null;
    try {
      freeTrialEndDateFormatted = freeTrialEndDate ? format(new Date(freeTrialEndDate), 'MMMM d, yyyy') : null;
    } catch (error) {
      freeTrialEndDateFormatted = null;
    }
    return {
      isFreeTrialStarted,
      freeTrialDays,
      freeTrialStartDate,
      freeTrialEndDate,
      freeTrialEndDateFormatted,
      freeTrialDaysRemaining,
      isFreeTrialExpired,
    };
  }, [
    currentUser?.organization_detail?.subscription_type,
    currentUser?.organization_detail?.is_free_trial_started,
    currentUser?.organization_detail?.free_trial_start_date,
    currentUser?.organization_detail?.free_trial_end_date,
    currentUser?.organization_detail?.is_free_trial_expired,
    currentUser?.organization_detail?.free_trial_days,
  ]);

  const userInfo = {
    // authorized: requiredAuthKeys, // it's based on old implementation, try to get rid of it
    currentUser: currentUser,
    currentUserId: currentUserId,
    setCurrentUser: setCurrentUser,
    orgOwnerDetail: orgOwnerDetail,
    setOrgOwnerDetail: setOrgOwnerDetail,
    setCurrentId: setCurrentUserId,
    updateCurrentUser: updateCurrentUser,
    token: token,
    setToken: setToken,
    refreshToken: refreshToken,
    setRefreshToken: setRefreshToken,
    verificationToken: verificationToken,
    setVerificationToken: setVerificationToken,
    onLogin: onLogin,
    logout: logout,
    isAdmin: currentUser?.role?.is_admin,
    isOwner: currentUser?.role?.is_owner,
    isAdminOrOwner: currentUser?.role?.is_admin || currentUser?.role?.is_owner,
    isNormalUser: currentUser?.role?.is_staff,
    isInternalUser: Boolean(currentUser?.role?.is_internal_member),
    isEditor: Boolean(currentUser?.is_editor), // who can edit room/props
    canDownload3DFile: Boolean(currentUser?.can_download_3d_file), // who can download 3d files of project,images,props and templates.
    canAccessLiveOnAppData: canAccessLiveOnAppData,
    canAccessLiveOnApp: canAccessLiveOnApp,
    canCreatePropGroup: canCreatePropGroup,
    canCreatePropGroupOrgLevel: canCreatePropGroupOrgLevel,
    canCreatePropGroupMemberLevel: canCreatePropGroupMemberLevel,
    canCreateUserpilotOnboardingSteps: Boolean(currentUser?.can_create_onboard_steps), // who can create the steps on Userpilot UI on curator
    canUseWorkflowBuilder: Boolean(
      currentUser?.can_use_workflow_builder || permissionsMap[PERMISSIONS.WORKFLOW_BUILDER.codename]
    ), // who can access the Workflow Builder tab/page
    isInternalOrg,
    latestTNCAccepted: latestTNCAccepted,
    setLatestTNCAccepted: setLatestTNCAccepted,
    emailVerified: emailVerified,
    setEmailVerified: setEmailVerified,
    changeProfilePic: changeProfilePic,
    changeOrgProfilePic: changeOrgProfilePic,
    addCreatedAtDate: addCreatedAtDate,
    changeWatermarkPic: changeWatermarkPic,
    inviteData: inviteData,
    setInviteData: setInviteData,
    freeCredits: currentUser?.organization_detail?.free_credits,
    userRegisteredOn: currentUser?.user?.userRegisteredOn,
    showAssignPackModal,
    setShowAssignPackModal,
    isPackOrganization: Boolean(currentUser?.organization_detail?.is_pack_organization),
    isUnlimitedRendering: Boolean(currentUser?.organization_detail?.is_unlimited_rendering),
    threeDModelUploadLimit: currentUser?.organization_detail?.model_upload_limit || 0,
    isNewSubscriptionPlan: Boolean(currentUser?.is_new_subscription_plan),
    isOldSubscriptionPlan: Boolean(currentUser?.is_old_subscription),
    isGuestLogin: Boolean(currentUser?.is_guest), // who can loggedin in our application from website's catalog page
    organizationType: getMyOrgType,
    updateUserData: updateUserData,
    isUpdatingUserDetails: isUpdatingUserDetails,
    isFreemiumUser,
    isPremiumUser,
    isOldSubscriptionUser,
    isFreemiumOrOldSubscriptionUser: isFreemiumUser() || isOldSubscriptionUser(),
    isFreemiumOrSelfServePlan,
    isPremiumAndSelfServePlan,
    isGuestUser,
    guestUserAccess,
    setGuestUserAccess,
    updateGuestUserAccess,
    subscription_end_date: currentUser?.organization_detail?.subscription_end_date,
    subscription_expiry: currentUser?.organization_detail?.subscription_expiry,
    isMemberHaveEditorAccess: Boolean(currentUser?.is_editor_access),
    permissionsMap,
    organizationPermissionsMap,
    logoutLoading,
    orgRegisteredOn: orgRegisteredOn,
    isSalsifyConnected: Boolean(currentUser?.organization_detail?.is_salsify_org),
    canUpdatePdpV1Content,
    freeTrialInfo,
  };

  window.userInfo = userInfo;
  window.currentUser = currentUser;

  return <UserContext.Provider value={userInfo}>{children}</UserContext.Provider>;
});

const useUserContext = () => useContext(UserContext);
export { UserContext, UserContextProvider, useUserContext };
